<template>
  <div class="flex-child">
    <div class="vue-chart">
      <el-skeleton :loading="loading" animated>
        <template #template>
          <el-skeleton-item style="height: 315px" />
        </template>
        <template #default>
          <apexchart
            v-if="!loading"
            width="100%"
            height="300"
            type="bar"
            :options="chartOptions"
            :series="radialSeries"
          />
        </template>
      </el-skeleton>
    </div>

    <div class="flex-text">
      <p v-html="$t('dashboardAvailableForPurchase', { label: formatters.label.call(currentTokenInfo?.name) })"></p>
      <p
        v-if="currentToken === Tokens.pearl"
        v-html="$t('dashboardReservedForLP', { label: formatters.label.call(currentTokenInfo?.name) })"
      ></p>
      <p
        v-if="currentToken === Tokens.pearl"
        v-html="$t('dashboardScatteredOnSeabed', { label: formatters.label.call(currentTokenInfo?.name) })"
      ></p>
      <p v-else v-html="$t('dashboardHiddenOnIsland', { label: formatters.label.call(currentTokenInfo?.name) })"></p>
      <p v-html="$t('dashboardInActiveCirculation', { label: formatters.label.call(currentTokenInfo?.name) })"></p>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { ChartDataFormatterProps } from '~/types/common';
import { Erc20Token__factory } from 'public/abi/evm/types';
import useProvider from '~/composables/useProvider';
import { Tokens } from '~/utils/constants';
import type { ITokenModel } from '~/types/apiService';
import useEnvs from '~/composables/useEnvs';
import { BigNumber } from 'bignumber.js';
import { formatEther } from 'ethers';
import { Formatters } from '~/utils/formatters/formatters';

const store = useMainStore();
const { blockchain } = useEnvs();
const { t } = useI18n();
const {
  saleAmount,
  soldAmount,
  liquidityAmount,
  tokenMaxTotalSupply,
  availableTokens,
  liquidityShare,
  totalStakingRewards
} = useTokensReader();
const { getProvider } = useProvider();

const { data: currentTokenInfo } = useNuxtData<ITokenModel>('dashboardCurrentTokenInfo');

const loading = ref(false);

defineProps<ChartDataFormatterProps>();

const currentToken = computed(() => store.currentToken);
const radialLabels = computed(() =>
  currentToken.value === Tokens.pearl
    ? [
        t('radialLabelAvailableForPurchase'),
        t('radialLabelReservedForLP'),
        t('radialLabelScatteredOnTheSeabed'),
        t('radialLabelInActiveCirculation')
      ]
    : [t('radialLabelAvailableForPurchase'), t('dashboardHiddenOnIslandLabel'), t('radialLabelInActiveCirculation')]
);
const saleAmountRef = ref(0);
const liquidityCurrentAmountRef = ref(0);
const tokenBalanceInMarketingRef = ref(0);
const maxTotalSupplyRef = ref(0);
const distributionLeftRef = ref(0);
const liquidityShareRef = asyncComputed(
  async () => (currentToken.value === Tokens.pearl ? await liquidityShare() : '0'),
  '0'
);
const distributionFondRef = asyncComputed(
  async () => await tokenMaxTotalSupply(currentToken.value).then((res) => formatEther(res || 0)),
  '0'
);
const walletBalanceRef = asyncComputed(
  async () =>
    await availableTokens(
      blockchain.contracts.pearlWallet,
      blockchain.contracts[currentToken.value].addresses.contract
    ),
  '0'
);
const totalRewardYieldRef = asyncComputed(async () => await totalStakingRewards(), '0');
const soldAmountRef = asyncComputed(async () => await soldAmount(currentToken.value), '0');
const tokenAddressTotalSupplyComputed = asyncComputed(async () => {
  const tokenContract = Erc20Token__factory.connect(
    blockchain.contracts[currentToken.value].addresses.contract,
    getProvider()
  );

  return await tokenContract.totalSupply().then((res) => formatEther(res || 0));
}, '0');

const circulationSum = computed(() =>
  currentToken.value === Tokens.pearl
    ? BigNumber(soldAmountRef.value).toNumber()
    : BigNumber(soldAmountRef.value).toNumber()
);

store.$subscribe(
  async (mutation, state) => {
    loading.value = true;

    try {
      const [
        tokenSaleAmount,
        tokenLiquidityCurrentAmount,
        tokenMaxTotalSupplyValue,
        tokenMarketingBalance,
        tokenTreasuryBalance
      ] = await Promise.allSettled([
        saleAmount(),
        liquidityAmount(),
        tokenMaxTotalSupply(),
        availableTokens(blockchain.contracts.marketing, blockchain.contracts[state.currentToken].addresses.contract),
        availableTokens(blockchain.contracts.treasury, blockchain.contracts[state.currentToken].addresses.contract)
      ]).then((results) => results.map((res) => (res.status === 'fulfilled' ? res.value : 0)));

      saleAmountRef.value = new BigNumber(tokenSaleAmount.toString()).integerValue().toNumber();
      liquidityCurrentAmountRef.value = new BigNumber(tokenLiquidityCurrentAmount.toString()).integerValue().toNumber();
      maxTotalSupplyRef.value = BigNumber(formatEther(tokenMaxTotalSupplyValue)).toNumber();
      tokenBalanceInMarketingRef.value = Number(tokenMarketingBalance);
      distributionLeftRef.value = Math.min(
        BigNumber(distributionFondRef.value).toNumber(),
        BigNumber(tokenTreasuryBalance.toString()).toNumber()
      );
    } catch (err) {
      console.log(err);
    } finally {
      loading.value = false;
    }
  },
  {
    immediate: true
  }
);

const radialSeries = computed(() => [
  {
    data:
      store.currentToken === Tokens.pearl
        ? [
            calculatePercentage(
              saleAmountRef.value,
              BigNumber(saleAmountRef.value).plus(soldAmountRef.value).toNumber(),
              2
            ),
            calculatePercentage(
              BigNumber(liquidityCurrentAmountRef.value).minus(liquidityShareRef.value).toNumber(),
              liquidityCurrentAmountRef.value,
              2
            ),
            calculatePercentage(
              BigNumber(walletBalanceRef.value).toNumber(),
              BigNumber(walletBalanceRef.value).plus(totalRewardYieldRef.value).toNumber(),
              2
            ),
            calculatePercentage(circulationSum.value, BigNumber(tokenAddressTotalSupplyComputed.value).toNumber(), 2)
          ]
        : [
            calculatePercentage(
              saleAmountRef.value,
              BigNumber(saleAmountRef.value).plus(soldAmountRef.value).toNumber(),
              2
            ),
            calculatePercentage(
              BigNumber(maxTotalSupplyRef.value).toNumber(),
              BigNumber(soldAmountRef.value).multipliedBy(2).toNumber(),
              2
            ),
            calculatePercentage(
              circulationSum.value,
              BigNumber(saleAmountRef.value).plus(soldAmountRef.value).toNumber(),
              2
            )
          ]
  }
]);

const chartOptions = computed(() => ({
  theme: {
    mode: 'dark'
  },
  grid: {
    borderColor: '#34414A'
  },

  chart: {
    type: 'bar',
    class: 'vuechart-example',
    background: '#00000000',
    height: 350,
    toolbar: {
      show: false
    },
    zoom: {
      enabled: false
    }
  },
  labels: radialLabels.value,
  title: {
    text: t('pearlDistributionHeading'),
    align: 'center',
    style: {
      fontSize: '24px'
    }
  },
  xaxis: {
    // categories: radialLabels.value,
    labels: {
      formatter: function (val: number) {
        return Formatters.number.renderString((f) => f.formatStaticPercent(val, 0)) + '%';
        //return val.toFixed(0) + '%';
      }
    },
    position: 'top'
  },
  yaxis: {
    min: 0,
    max: 100,

    labels: {
      show: false
    }
  },
  dataLabels: {
    enabled: true,
    formatter: function (val: number) {
      return Formatters.number.renderString((f) => f.formatStaticPercent(val, 0)) + '%';
    }
  },
  colors: ['#1EBFFD', '#FD338A', '#56FFC1', '#8E64F6', '#FD8557', '#072530'],
  plotOptions: {
    bar: {
      distributed: true,
      borderRadius: 4,
      borderRadiusApplication: 'end',
      horizontal: true
    }
  },
  tooltip: {
    enabled: true,
    x: { show: false },
    y: {
      formatter: function (val: number) {
        return Formatters.number.renderString((f) => f.formatPercent(val, 2)) + '%';
      },
      title: {
        formatter: () => ''
      }
    }
  },
  legend: {
    onItemHover: {
      highlightDataSeries: true
    }
  }
}));
</script>

<style lang="scss" scoped>
.vue-chart {
  min-height: 340px;
}
</style>
